import React, { useState, useEffect } from "react";
import { Edit, Delete } from "lucide-react";
import { ChevronDown } from "lucide-react";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";

export default function AllUniversities({ theme }) {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [actionMenu, setActionMenu] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [residencies, setResidencies] = useState([]);
  const [residencyError, setResidencyError] = useState(false);
  const [openResidencyId, setOpenResidencyId] = useState(null);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingAllCities, setLoadingAllCities] = useState(false);

  const fetchFormOptions = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/form-options`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!res.ok) {
        throw new Error("Failed to fetch form options");
      }
      const data = await res.json();
      setCountries(data.countries || []);
    } catch (err) {
      console.error("Error fetching form options:", err);
      setAlert({
        type: "error",
        message: "Failed to load countries.",
      });
    }
  };

  const fetchAllCities = async () => {
    setLoadingAllCities(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-cities_1`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!res.ok) {
        throw new Error("Failed to fetch cities");
      }
      const data = await res.json();
      setAllCities(data.data);
    } catch (err) {
      console.error("Error fetching cities:", err);
      setAllCities([]);
    } finally {
      setLoadingAllCities(false);
    }
  };

  const fetchCitiesByCountry = async (countryId) => {
    if (!countryId) {
      setCities([]);
      return;
    }
    
    setLoadingCities(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/c_cities/${countryId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!res.ok) {
        throw new Error("Failed to fetch cities");
      }
      const data = await res.json();
      setCities(data.cities || []);
    } catch (err) {
      console.error("Error fetching cities:", err);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchResidencies = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/residency-status`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `HTTP ${res.status} - Failed to fetch residency statuses`
        );
      }
      const data = await res.json();
      const activeResidencies = data.filter((res) => res.status === 1);
      setResidencies(activeResidencies);
      setResidencyError(false);
    } catch (err) {
      console.error("Error fetching residency statuses:", err);
      setAlert({
        type: "error",
        message: err.message || "Failed to load residency statuses.",
      });
      setResidencyError(true);
    }
  };

  const fetchUniversities = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/universities`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${res.status} - Failed to fetch universities`
        );
      }
      const data = await res.json();
      const universitiesData = data.data || [];

      const mapped = universitiesData.map((item) => ({
        id: item.id,
        name: item.university,
        residency_ids: item.residency_ids || [],
        residencies: item.residencies || ["N/A"],
        status: item.status === 1 ? "Active" : "Inactive",
        city_id: item.city_id || null,
        city_name: item.city_name || "N/A",
        country_id: item.country_id || null,
        country_name: item.country_name || "N/A",
        createdDate: item.created_at
          ? new Date(item.created_at).toLocaleDateString()
          : "N/A",
        updatedDate: item.updated_at
          ? new Date(item.updated_at).toLocaleDateString()
          : "N/A",
      }));

      setUniversities(mapped);
    } catch (err) {
      console.error("Error fetching universities:", err);
      setAlert({
        type: "error",
        message: err.message || "Failed to load universities.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isInternationalResidency = (residencyIds) => {
    if (!residencyIds || !residencyIds.length) return false;
    const selectedResidencies = residencies.filter(res => 
      residencyIds.includes(res.id)
    );
    return selectedResidencies.some(res => 
      res.residency?.toLowerCase().includes('international')
    );
  };

  useEffect(() => {
    fetchUniversities();
    fetchResidencies();
    fetchFormOptions();
    fetchAllCities();
  }, []);

  useEffect(() => {
    if (editModal?.country_id) {
      fetchCitiesByCountry(editModal.country_id);
    } else {
      setCities([]);
    }
  }, [editModal?.country_id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".residency-dropdown")) {
        setOpenResidencyId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest(".action-menu-trigger") &&
        !e.target.closest(".action-menu")
      ) {
        setActionMenu(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (alert.message) {
      const timer = setTimeout(() => setAlert({ type: "", message: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  const filteredUniversities = universities.filter(
    (uni) =>
      uni.name.toLowerCase().includes(filter.toLowerCase()) ||
      (uni.city_name &&
        uni.city_name.toLowerCase().includes(filter.toLowerCase())) ||
      (uni.country_name &&
        uni.country_name.toLowerCase().includes(filter.toLowerCase()))
  );

  const toggleMenu = (id, e) => {
    if (e) e.stopPropagation();
    setActionMenu(actionMenu === id ? null : id);
  };

  const deleteUniversity = async (id) => {
    setWorkingId(id);
    setAlert({ type: "", message: "" });
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/delete-universities/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || data.message || "Delete failed.");
      }

      setAlert({
        type: "success",
        message: data.message || "University deleted successfully.",
      });
      setUniversities((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      console.error("Delete error:", err);
      setAlert({ 
        type: "error", 
        message: err.message || "Failed to delete university. Please try again." 
      });
    } finally {
      setWorkingId(null);
      setConfirmDelete(null);
    }
  };

  const updateUniversity = async (id, updatedData) => {
    setWorkingId(id);
    setAlert({ type: "", message: "" });

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/universities/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            university: updatedData.university,
            residency_ids: updatedData.residency_ids || [],
            city_id: updatedData.city_id || null,
          }),
        }
      );
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Update failed.");

      setAlert({
        type: "success",
        message: "University updated successfully.",
      });
      await fetchUniversities();
    } catch (err) {
      setAlert({ type: "error", message: err.message || "Update failed." });
    } finally {
      setWorkingId(null);
      setEditModal(null);
    }
  };

  const updateStatus = async (id, newStatus) => {
    setWorkingId(id);
    setAlert({ type: "", message: "" });

    const oldStatus = universities.find((u) => u.id === id)?.status ?? "Inactive";

    setUniversities((prev) =>
      prev.map((u) => (u.id === id ? { ...u, status: newStatus } : u))
    );

    try {
      const isActive = newStatus === "Active";
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/status-universities/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ status: isActive ? 1 : 0 }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Status update failed.");

      setAlert({ type: "success", message: "Status updated successfully." });
    } catch (err) {
      setUniversities((prev) =>
        prev.map((u) => (u.id === id ? { ...u, status: oldStatus } : u))
      );
      setAlert({
        type: "error",
        message: err.message || "Status update failed.",
      });
    } finally {
      setWorkingId(null);
    }
  };

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full flex items-center justify-center p-6"
      >
        <div className="flex items-center gap-2">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"
          />
          <div className="text-sm text-gray-600">Loading universities…</div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative p-4 bg-white rounded-lg shadow-sm border border-gray-200"
    >
      <h2 className="text-xl font-bold mb-1 text-gray-800">University List</h2>
      <p className="text-xs text-gray-600 mb-4">Browse All Universities</p>

      {/* Toast container */}
      <AnimatePresence>
        {alert.message && (
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            transition={{ duration: 0.3 }}
            className="fixed top-3 right-3 z-50"
          >
            <div
              className={`max-w-xs w-full ${
                alert.type === "error"
                  ? "bg-red-50 border-red-300 text-red-700"
                  : "bg-green-50 border-green-300 text-green-700"
              } border rounded-lg shadow-lg overflow-hidden text-xs`}
            >
              <div className="flex items-start p-3">
                <div className="flex-shrink-0 mr-2">
                  {alert.type === "error" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 00-1.414-1.414L8 11.172 4.707 7.879a1 1 0 10-1.414 1.414l4 4a1 1 0 001.414 0l8-8z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1">{alert.message}</div>
                <button
                  className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  onClick={() => setAlert({ type: "", message: "" })}
                  aria-label="Dismiss toast"
                >
                  ×
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Delete Modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-800 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-40"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-4 max-w-xs w-full mx-4 shadow-xl"
            >
              <h3 className="text-base font-semibold mb-3 text-gray-800">Confirm Delete</h3>
              <p className="text-xs mb-4 text-gray-600">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-red-600">{confirmDelete.name}</span>?
                This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => deleteUniversity(confirmDelete.id)}
                  className="bg-red-600 text-white px-3 py-1.5 text-sm rounded hover:bg-red-700 focus:outline-none transition-colors duration-200 disabled:opacity-50"
                  disabled={workingId === confirmDelete.id}
                >
                  {workingId === confirmDelete.id ? (
                    <div className="flex items-center gap-1.5">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-3 h-3 border-2 border-white border-t-transparent rounded-full"
                      />
                      Deleting...
                    </div>
                  ) : (
                    "Delete"
                  )}
                </button>
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="bg-gray-200 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-300 focus:outline-none transition-colors duration-200"
                  disabled={workingId === confirmDelete.id}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit University Modal */}
      <AnimatePresence>
        {editModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-800 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-40"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-4 max-w-sm w-full mx-4 shadow-xl max-h-[85vh] overflow-y-auto"
            >
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Edit University</h3>
              <div>
                <div className="mb-3">
                  <label className="block text-xs font-medium mb-1.5 text-gray-700">
                    University Name
                  </label>
                  <input
                    type="text"
                    value={editModal.name}
                    onChange={(e) =>
                      setEditModal({ ...editModal, name: e.target.value })
                    }
                    className="w-full border border-gray-300 px-3 py-1.5 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter university name"
                  />
                </div>

                <div className="mb-3">
                  <label className="block text-xs font-medium mb-1.5 text-gray-700">
                    Residency
                  </label>
                  <Select
                    isMulti
                    isDisabled={residencyError || residencies.length === 0}
                    options={residencies.map((res) => ({
                      value: res.id,
                      label: res.residency,
                    }))}
                    value={
                      editModal.residency_ids && editModal.residency_ids.length > 0
                        ? residencies
                            .filter((res) => editModal.residency_ids.includes(res.id))
                            .map((r) => ({
                              value: r.id,
                              label: r.residency,
                            }))
                        : []
                    }
                    onChange={(selected) => {
                      const newResidencyIds = selected.map((s) => s.value);
                      setEditModal({
                        ...editModal,
                        residency_ids: newResidencyIds,
                        country_id: isInternationalResidency(newResidencyIds) ? editModal.country_id : null,
                      });
                    }}
                    placeholder={
                      residencyError
                        ? "Residencies unavailable"
                        : "Select residencies"
                    }
                    classNamePrefix="react-select"
                    className="react-select-container text-sm"
                    styles={{
                      control: (base) => ({
                        ...base,
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                        padding: "1px",
                        fontSize: "0.875rem",
                        minHeight: "32px",
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: "0 8px",
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                      option: (base) => ({
                        ...base,
                        fontSize: "0.875rem",
                        padding: "6px 12px",
                      }),
                    }}
                  />
                  {residencyError && (
                    <p className="text-red-500 text-xs mt-1">
                      Failed to load residencies.
                    </p>
                  )}
                </div>

                {isInternationalResidency(editModal.residency_ids) && (
                  <div className="mb-3">
                    <label className="block text-xs font-medium mb-1.5 text-gray-700">
                      Country
                    </label>
                    <select
                      value={editModal.country_id || ""}
                      onChange={(e) => {
                        const countryId = e.target.value || null;
                        setEditModal({ 
                          ...editModal, 
                          country_id: countryId,
                        });
                        if (countryId) {
                          fetchCitiesByCountry(countryId);
                        }
                      }}
                      className="w-full border border-gray-300 px-3 py-1.5 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.nicename}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-xs font-medium mb-1.5 text-gray-700">
                    City
                  </label>
                  <select
                    value={editModal.city_id || ""}
                    onChange={(e) =>
                      setEditModal({ ...editModal, city_id: e.target.value || null })
                    }
                    disabled={loadingAllCities || (isInternationalResidency(editModal.residency_ids) && loadingCities)}
                    className="w-full border border-gray-300 px-3 py-1.5 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-50"
                  >
                    <option value="">Select City</option>
                    {isInternationalResidency(editModal.residency_ids) ? (
                      cities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.city_name}
                        </option>
                      ))
                    ) : (
                      allCities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.city_name}
                        </option>
                      ))
                    )}
                  </select>
                  {(loadingAllCities || (isInternationalResidency(editModal.residency_ids) && loadingCities)) && (
                    <p className="text-blue-500 text-xs mt-1">Loading cities...</p>
                  )}
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      updateUniversity(editModal.id, {
                        university: editModal.name,
                        residency_ids: editModal.residency_ids || [],
                        city_id: editModal.city_id || null,
                        country_id: editModal.country_id || null,
                      });
                    }}
                    className="bg-blue-600 text-white px-3 py-1.5 text-sm rounded hover:bg-blue-700 focus:outline-none disabled:opacity-50 transition-all duration-200"
                    disabled={
                      workingId === editModal.id ||
                      residencyError ||
                      !editModal.name.trim() ||
                      !editModal.residency_ids ||
                      editModal.residency_ids.length === 0 ||
                      !editModal.city_id ||
                      (isInternationalResidency(editModal.residency_ids) && !editModal.country_id)
                    }
                  >
                    {workingId === editModal.id ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-3 h-3 border-2 border-white border-t-transparent rounded-full mx-auto"
                      />
                    ) : (
                      "Save"
                    )}
                  </button>

                  <button
                    onClick={() => setEditModal(null)}
                    className="bg-gray-200 text-gray-700 px-3 py-1.5 text-sm rounded hover:bg-gray-300 focus:outline-none transition-all duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between mb-4">
        <motion.input
          whileFocus={{ scale: 1.01 }}
          type="text"
          className="border border-gray-300 px-3 py-1.5 rounded text-sm w-1/2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          placeholder="Search by university, city, or country..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <span className="text-xs text-gray-600">
          {filteredUniversities.length} of {universities.length}
        </span>
      </div>

      <motion.div 
        layout
        className="overflow-x-auto rounded-lg shadow-sm border border-gray-200 bg-white"
      >
        <table className="w-full text-xs border-collapse">
          <thead className="bg-gray-50 text-gray-700 uppercase">
            <tr>
              {[
                "#",
                "University",
                "City",
                "Country",
                "Residency",
                "Created",
                "Updated",
                "Status",
                "Actions",
              ].map((head, idx) => (
                <th key={idx} className="p-2 border-b border-gray-200 text-center font-semibold">
                  {head}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {!filteredUniversities?.length ? (
              <tr>
                <td
                  colSpan={9}
                  className="p-6 text-center text-gray-500 italic text-sm"
                >
                  No universities found matching your search.
                </td>
              </tr>
            ) : (
              filteredUniversities.map((uni, index) => (
                <motion.tr
                  key={uni.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.02 }}
                  className="hover:bg-gray-50 even:bg-gray-50/30 transition-colors duration-150 group"
                >
                  <td className="p-2 border-b border-gray-100 text-center font-medium text-gray-600">
                    {index + 1}
                  </td>

                  <td className="p-2 border-b border-gray-100 font-semibold text-blue-600 group-hover:text-blue-700 transition-colors max-w-[120px] truncate">
                    {uni.name}
                  </td>

                  <td className="p-2 border-b border-gray-100 text-gray-700 max-w-[80px] truncate">
                    {uni.city_name === "N/A" ? (
                      <span className="text-gray-400 italic">N/A</span>
                    ) : (
                      uni.city_name
                    )}
                  </td>

                  <td className="p-2 border-b border-gray-100 text-gray-700 max-w-[80px] truncate">
                    {uni.country_name === "N/A" ? (
                      <span className="text-gray-400 italic">N/A</span>
                    ) : (
                      uni.country_name
                    )}
                  </td>

                  <td className="p-2 border-b border-gray-100 relative residency-dropdown">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenResidencyId(
                          openResidencyId === uni.id ? null : uni.id
                        );
                      }}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium transition-colors text-xs"
                    >
                      View
                      <ChevronDown
                        className={`h-3 w-3 transition-transform duration-200 ${
                          openResidencyId === uni.id ? "rotate-180" : ""
                        }`}
                      />
                    </motion.button>

                    <AnimatePresence>
                      {openResidencyId === uni.id && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          className="absolute left-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded shadow-lg z-10 p-2"
                        >
                          <ul className="text-gray-700 space-y-0.5">
                            {uni.residencies && uni.residencies.length > 0 ? (
                              uni.residencies.map((res, i) => (
                                <motion.li
                                  key={i}
                                  initial={{ opacity: 0, x: -5 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.05 }}
                                  className="px-1.5 py-1 hover:bg-blue-50 rounded transition-colors text-xs"
                                >
                                  {res}
                                </motion.li>
                              ))
                            ) : (
                              <li className="px-1.5 py-1 text-gray-500 italic text-xs">
                                No residencies
                              </li>
                            )}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </td>

                  <td className="p-2 border-b border-gray-100 text-center text-gray-500">
                    {uni.createdDate}
                  </td>
                  <td className="p-2 border-b border-gray-100 text-center text-gray-500">
                    {uni.updatedDate}
                  </td>

                  <td className="p-2 border-b border-gray-100 text-center">
                    <motion.select
                      whileHover={{ scale: 1.03 }}
                      whileFocus={{ scale: 1.03 }}
                      value={uni.status}
                      onChange={(e) => updateStatus(uni.id, e.target.value)}
                      disabled={workingId === uni.id}
                      className={`px-2 py-1 rounded text-xs font-semibold border outline-none transition-all duration-150 disabled:opacity-50 cursor-pointer ${
                        uni.status === "Active"
                          ? "text-green-700 bg-green-100 border-green-400 hover:bg-green-200"
                          : "text-red-700 bg-red-100 border-red-400 hover:bg-red-200"
                      }`}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </motion.select>
                  </td>

                  <td className="p-2 border-b border-gray-100 text-center relative">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMenu(uni.id, e);
                      }}
                      disabled={workingId === uni.id}
                      className="p-1 text-gray-600 hover:text-blue-600 transition-colors duration-150 disabled:opacity-50 action-menu-trigger rounded hover:bg-blue-50"
                    >
                      {workingId === uni.id ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"
                        />
                      ) : (
                        <span className="text-sm">⚙</span>
                      )}
                    </motion.button>

                    <AnimatePresence>
                      {actionMenu === uni.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          className="absolute right-1 mt-1 w-28 bg-white border border-gray-200 rounded shadow-lg z-10 action-menu overflow-hidden"
                        >
                          <motion.button
                            whileHover={{ backgroundColor: "#f8fafc" }}
                            onClick={() => {
                              setEditModal({
                                id: uni.id,
                                name: uni.name,
                                residency_ids: uni.residency_ids || [],
                                city_id: uni.city_id || null,
                                country_id: uni.country_id || null,
                              });
                              setActionMenu(null);
                            }}
                            disabled={residencyError}
                            className={`flex items-center gap-1.5 px-2 py-1.5 w-full text-left transition-colors text-xs ${
                              residencyError
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-blue-600"
                            }`}
                          >
                            <Edit size={12} /> Edit
                          </motion.button>

                          <motion.button
                            whileHover={{ backgroundColor: "#fef2f2" }}
                            onClick={() => {
                              setConfirmDelete({ id: uni.id, name: uni.name });
                              setActionMenu(null);
                            }}
                            className="flex items-center gap-1.5 px-2 py-1.5 w-full text-left text-red-600 transition-colors text-xs"
                          >
                            <Delete size={12} /> Delete
                          </motion.button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </motion.div>
    </motion.div>
  );
}